import { Search } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"

interface HeroSectionProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  totalIntegrations: number
}

/**
 * Hero section for the Integrations product page.
 * Features a search box for finding integrations.
 */
export function HeroSection({ searchQuery, onSearchChange, totalIntegrations }: HeroSectionProps) {
  return (
    <section className="relative overflow-hidden bg-neutral-900 pt-24 pb-16 lg:pt-32 lg:pb-24">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Text Content - Centered */}
        <div className="text-center">
          <Badge className="mb-6 border-primary/30 bg-primary/10 text-primary">
            {totalIntegrations}+ Integrations
          </Badge>
          <h1 className="mb-6 text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            Connect to Your{" "}
            <span className="text-primary">Favorite Apps</span>
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg text-neutral-400">
            Seamlessly integrate with the tools you already use. From CRM and marketing
            to communication and productivity - we&apos;ve got you covered.
          </p>

          {/* Search Box */}
          <div className="relative mx-auto max-w-xl">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
            <Input
              type="text"
              placeholder="Search integrations... (e.g., Slack, Gmail, Salesforce)"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-14 rounded-xl border-neutral-700 bg-neutral-800 pl-12 pr-4 text-white placeholder:text-neutral-500 focus:border-primary focus:ring-primary"
            />
          </div>
        </div>
      </div>
    </section>
  )
}
